import { Button, Card, Field, Input, Typography } from "@norican/react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { HelpdeskContactLine, LoadingState } from "@/components/common";
import { Microsoft } from "@/components/icons/microsoft";
import { usePublicDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";
import { logger } from "@/lib/logger";
import { safeReturnPath } from "@/lib/safe-return-path";
import { claimSsoRetry, type RetryStore, shouldAutoSignIn, SSO_RETRY_ERROR } from "@/lib/sso-retry";

// Local dev can't complete a real Microsoft sign-in (Entra won't redirect back
// to localhost), so the dev server shows a login picker that hits the
// backend's `/auth/dev-login` instead — a genuine session, just without
// Microsoft. Production builds always show the Microsoft button; the backend
// refuses `/auth/dev-login` there regardless (see DEV_AUTH_BYPASS).
const SHOW_DEV_LOGIN = import.meta.env.DEV;

// The last address used for a dev sign-in, so a developer testing against the
// real Ivanti UAT tenant as themselves doesn't retype it every session.
const DEV_LOGIN_EMAIL_KEY = "it-helpdesk-agent.dev-login-email";

function sessionStore(): RetryStore | null {
  try {
    return window.sessionStorage;
  } catch (err) {
    logger.debug("[login] sessionStorage unavailable:", err);
    return null;
  }
}

// The `location.search` a retry was granted for in this page load. The claim
// writes to storage, so a second call (StrictMode's double render, a remount)
// would say no; remembering the grant keeps the answer stable until the
// redirect takes the page away.
let retryGrantedFor: string | null = null;

/** Whether this visit should silently retry Microsoft sign-in. Claims once. */
function shouldRetrySso(ssoError: string | null): boolean {
  if (ssoError !== SSO_RETRY_ERROR) {
    return false;
  }
  const key = window.location.search;
  if (retryGrantedFor === key) {
    return true;
  }
  if (claimSsoRetry(sessionStore(), Date.now())) {
    retryGrantedFor = key;
    return true;
  }
  return false;
}

function readRememberedEmail(): string {
  try {
    return window.localStorage.getItem(DEV_LOGIN_EMAIL_KEY) ?? "";
  } catch (err) {
    logger.debug("[login] localStorage unavailable:", err);
    return "";
  }
}

function rememberEmail(email: string): void {
  try {
    if (email === "") {
      window.localStorage.removeItem(DEV_LOGIN_EMAIL_KEY);
    } else {
      window.localStorage.setItem(DEV_LOGIN_EMAIL_KEY, email);
    }
  } catch (err) {
    logger.debug("[login] could not persist the dev sign-in email:", err);
  }
}

export function LoginPage() {
  const { t } = useT();
  usePublicDocumentTitle(t("login.subtitle"));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  // Set when Microsoft sign-in itself failed: nothing on this page can fix
  // that, so the page says who can.
  const [ssoFailed, setSsoFailed] = useState(false);
  const [devSigningIn, setDevSigningIn] = useState(false);
  const [devEmail, setDevEmail] = useState(readRememberedEmail);
  // Where the visitor was going before `ProtectedRoute` sent them here. Checked
  // here and again by the backend: it is a query parameter, so anyone can write it.
  const returnPath = safeReturnPath(searchParams.get("next"));
  // Decided before the first paint, so the card never flashes up on its way
  // to Microsoft (see `lib/sso-retry.ts` for the 60 s guard).
  const [retryingSso] = useState(() => shouldRetrySso(searchParams.get("error")));
  // Straight to Microsoft, no button (28 Oct): Microsoft still has the person
  // signed in to Windows/Office, so this usually finishes without a prompt.
  const [autoSignIn] = useState(() => shouldAutoSignIn(searchParams, SHOW_DEV_LOGIN));

  const handleMicrosoftLogin = () => {
    logger.debug("[login] redirecting to Microsoft SSO, returning to", returnPath);
    window.location.assign(`/api/auth/sso/login?next=${encodeURIComponent(returnPath)}`);
  };

  const handleDevLogin = async (role: "user" | "migrator" | "admin") => {
    const email = devEmail.trim();
    logger.debug("[login] dev sign-in as", role, email === "" ? "(default identity)" : email);
    setError("");
    setDevSigningIn(true);
    // Blank keeps the backend's `dev-<role>@localhost` identity.
    const params = new URLSearchParams({ role });
    if (email !== "") {
      params.set("email", email);
    }
    try {
      const response = await fetch(`/api/auth/dev-login?${params.toString()}`, {
        method: "POST",
      });
      if (response.status === 422 && email !== "") {
        logger.debug("[login] dev sign-in rejected the email:", email);
        setError("That email isn't a valid address in an allowed domain.");
        setDevSigningIn(false);
        return;
      }
      if (!response.ok) {
        throw new Error(`dev-login responded ${response.status}`);
      }
      rememberEmail(email);
      logger.debug("[login] dev sign-in succeeded → redirecting to", returnPath);
      void navigate(returnPath, { replace: true });
    } catch (err) {
      logger.debug("[login] dev sign-in failed:", err);
      setError(t("login.error.devFailed"));
      setDevSigningIn(false);
    }
  };

  useEffect(() => {
    // `domain_not_allowed` gets its own wording; `sso_failed` and anything
    // else the callback sends get the generic one.
    const ssoError = searchParams.get("error");
    if (!ssoError) {
      return;
    }
    logger.debug("[login] sso error:", ssoError);
    if (retryingSso) {
      // Our half of the handshake failed (see `lib/sso-retry.ts`); Microsoft
      // still has them signed in, so this finishes without a click.
      logger.debug("[login] retrying sign-in once, returning to", returnPath);
      window.location.replace(`/api/auth/sso/login?next=${encodeURIComponent(returnPath)}`);
      return;
    }
    setError(
      ssoError === "domain_not_allowed"
        ? t("login.error.domainNotAllowed")
        : t("login.error.generic"),
    );
    setSsoFailed(true);
  }, [searchParams, t, returnPath, retryingSso]);

  useEffect(() => {
    if (autoSignIn) {
      logger.debug("[login] signing in automatically, returning to", returnPath);
      window.location.replace(`/api/auth/sso/login?next=${encodeURIComponent(returnPath)}`);
    }
  }, [autoSignIn, returnPath]);

  if (retryingSso || autoSignIn) {
    return (
      <div className="bg-bg flex min-h-screen items-center justify-center px-4">
        <LoadingState label={t("login.signingIn")} description={null} />
      </div>
    );
  }

  return (
    <div className="bg-bg flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-md">
        {/* Stacked, not the header's default row: side by side on a phone the
            product name wrapped over two lines with the subtitle squeezed
            beside it. */}
        <Card.Header className="flex-col items-center gap-1 text-center">
          <Typography as="h1" variant="heading-2" className="text-balance">
            {t("app.name")}
          </Typography>
          <p className="text-fg-muted text-sm">{t("login.subtitle")}</p>
        </Card.Header>
        <Card.Body>
          {SHOW_DEV_LOGIN ? (
            <div className="space-y-2">
              <p className="text-fg-muted text-center text-xs">
                Local dev — Microsoft sign-in only works on the deployed app.
              </p>
              <Field
                name="dev-login-email"
                label="Sign in as (optional)"
                hint="Your Norican address, to see your own tickets on the Ivanti tenant. Leave blank for a local test user."
                disabled={devSigningIn}
              >
                <Input
                  name="dev-login-email"
                  type="email"
                  autoComplete="email"
                  placeholder="firstname.lastname@noricangroup.com"
                  value={devEmail}
                  onChange={(event) => setDevEmail(event.target.value)}
                />
              </Field>
              <Button
                type="button"
                disabled={devSigningIn}
                onClick={() => handleDevLogin("user")}
                fullWidth
              >
                Continue as User
              </Button>
              <Button
                type="button"
                disabled={devSigningIn}
                variant="outline"
                onClick={() => handleDevLogin("migrator")}
                fullWidth
              >
                Continue as Migrator
              </Button>
              <Button
                type="button"
                disabled={devSigningIn}
                variant="outline"
                onClick={() => handleDevLogin("admin")}
                fullWidth
              >
                Continue as Admin
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              onClick={handleMicrosoftLogin}
              startIcon={<Microsoft className="size-4" />}
              fullWidth
            >
              {t("login.microsoft")}
            </Button>
          )}
          {error && (
            <p role="alert" className="text-danger-fg mt-4 text-center text-sm">
              {error}
            </p>
          )}
          {ssoFailed ? <HelpdeskContactLine className="mt-3 items-center text-center" /> : null}
        </Card.Body>
      </Card>
    </div>
  );
}
